import { getCldImageUrl, GetCldImageUrlOptions } from 'next-cloudinary';

import {
  inTransitDefaultImageUrl,
  INVENTORY_IMAGE_HEIGHT,
  INVENTORY_IMAGE_WIDTH,
  noPhotoDefaultImageUrl,
} from '@/constants/inventory';
import {
  BaseFilters,
  FilterPromotion,
  FilterPromotionInventoryField,
  Filters,
  FilterSection,
  FilterValue,
  getInventoryType,
  RangeFilterValue,
  revertInventoryType,
  SortBy,
} from '@/types/inventory';
import { ILayoutContextData } from '@/types/layout';
import {
  convertStructureToFilters,
  getFacetCode,
  getMultipleFilter,
  getRangeFilter,
  getSingleFilter,
} from '@lamesarv-sdk/tools';
import { IBasicInventory, IInventoryPriceFlag, InventoryField } from '@lamesarv-sdk/types';

export const getDefaultSort = (context: ILayoutContextData, customGeoLocation?: [number, number]) => {
  const sortList = [`${InventoryField.order}:asc`];

  if (customGeoLocation)
    sortList.unshift(`${InventoryField.geoLocation}(${customGeoLocation[0]},${customGeoLocation[1]}):asc`);

  if (context.geoLocation)
    sortList.unshift(`${InventoryField.geoLocation}(${context.geoLocation[0]},${context.geoLocation[1]}):asc`);

  return sortList.slice(0, 2).join(',');
};

export const isNewUnit = (item: { [InventoryField.inventoryType] }) =>
  item[InventoryField.inventoryType].toLowerCase() === 'new';

export const getListPriceLabel = (item: IBasicInventory): string => {
  return isNewUnit(item) ? 'MSRP' : 'Was';
};

export const getUnlockPriceLabel = (item: IBasicInventory) => {
  if (isNewUnit(item)) {
    return {
      buttonLabel:
        item[InventoryField.priceFlag] === IInventoryPriceFlag.windowPrice
          ? 'Lock in Today’s Price'
          : 'Unlock Today’s Price',
      subtext:
        item[InventoryField.priceFlag] === IInventoryPriceFlag.windowPrice
          ? 'We’ll get back to you right away with today’s best pricing.'
          : 'We’ll get back to you right away.',
    };
  }

  const listPrice = item[InventoryField.priceList];
  const basePrice = item[InventoryField.price] === listPrice ? undefined : item[InventoryField.price];

  return {
    buttonLabel: !!basePrice ? "Lock in Today's Price" : 'Unlock Today’s Price',
    subtext: !!basePrice
      ? 'We’ll get back to you right away with today’s best pricing.'
      : 'We’ll get back to you right away.',
  };
};

export const getImageUrl = (
  src: string,
  width: number,
  height: number,
  extraOptions?: Partial<GetCldImageUrlOptions>,
) =>
  getCldImageUrl({
    src,
    width,
    height,
    ...(extraOptions || {}),
  });

export const isInStock = (item: IBasicInventory) => !!item[InventoryField.daysInStock];

export const getDefaultImageUrl = (item: IBasicInventory) => {
  if (isInStock(item)) return noPhotoDefaultImageUrl;

  if (item[InventoryField.defaultImageUrl]) return item[InventoryField.defaultImageUrl];

  return inTransitDefaultImageUrl;
};

export const getUniqueInventoryImageUrl = (item: IBasicInventory, customImage?: string, useCloudinaryIds = false) => {
  if (customImage) return customImage;

  if (useCloudinaryIds && item[InventoryField.imageCloudinaryIds]?.length)
    return getImageUrl(item[InventoryField.imageCloudinaryIds][0], INVENTORY_IMAGE_WIDTH, INVENTORY_IMAGE_HEIGHT);

  if (item[InventoryField.thumbnails]?.length) return item[InventoryField.thumbnails][0];
  if (item[InventoryField.images]?.length) return item[InventoryField.images][0];

  return getDefaultImageUrl(item);
};

type FilterSectionConverterResult = { field: string; value: string[] | boolean | RangeFilterValue };
type FilterSectionConverter = (value: FilterValue, baseFilters: BaseFilters) => FilterSectionConverterResult[];

const getDefaultFilterSectionConverter =
  (field: string): FilterSectionConverter =>
  (value) => {
    if (!value) return [];

    return [
      {
        field,
        value,
      },
    ];
  };

const getUniqueFilterSectionConverter =
  (field: InventoryField): FilterSectionConverter =>
  (value, baseFilters) => {
    if (!value || !(value instanceof Array) || value.length === 0) return [];

    const facetValuesMap = new Map<string, string[]>();

    for (const option of value) {
      const facetValue = getFacetCode(option);

      facetValuesMap.set(facetValue, []);
    }

    const baseValues = baseFilters[field] || [];

    for (const baseValue of baseValues) {
      const facetValue = getFacetCode(baseValue);

      if (facetValuesMap.has(facetValue)) {
        facetValuesMap.get(facetValue).push(baseValue);
      }
    }

    return [
      {
        field,
        value: Array.from(facetValuesMap.values()).flat(),
      },
    ];
  };

const customFilterSectionConverter: Partial<Record<FilterSection, FilterSectionConverter>> = {
  [FilterSection.RV_TYPE]: getDefaultFilterSectionConverter(InventoryField.body),
  [FilterSection.MAKE]: getUniqueFilterSectionConverter(InventoryField.make),
  [FilterSection.MODEL]: getUniqueFilterSectionConverter(InventoryField.model),
  [FilterSection.FLOOR_PLAN]: getUniqueFilterSectionConverter(InventoryField.modelNumber),
  [FilterSection.FEATURES]: getUniqueFilterSectionConverter(InventoryField.equipment),
  [FilterSection.LOCATION]: getDefaultFilterSectionConverter(InventoryField.location),
  [FilterSection.FUEL_TYPE]: getDefaultFilterSectionConverter(InventoryField.fuelType),
  [FilterSection.SLIDEOUTS]: getDefaultFilterSectionConverter(InventoryField.slideOuts),
  [FilterSection.CONDITION]: (value) => {
    if (value instanceof Array)
      return [
        {
          field: InventoryField.inventoryType,
          value: value.map(revertInventoryType),
        },
      ];

    return [];
  },
  [FilterSection.PROMOTIONS]: (value) => {
    if (!(value instanceof Array) || value.length === 0) return [];

    return [
      {
        field: FilterSection.PROMOTIONS,
        value,
      },
    ];
  },
  [FilterSection.LENGTH]: getDefaultFilterSectionConverter(InventoryField.length),
  [FilterSection.PRICE]: getDefaultFilterSectionConverter(InventoryField.price),
  [FilterSection.YEAR]: getDefaultFilterSectionConverter(InventoryField.modelYear),
};

export const convertFilterSection = (filters: Filters, baseFilters: BaseFilters) => {
  const structuredFilters: Partial<Record<string, string[] | boolean | RangeFilterValue>> = {};

  for (const [section, value] of Object.entries(filters)) {
    const converter = customFilterSectionConverter[section as FilterSection];

    if (!converter) continue;

    const converted = converter(value, baseFilters);

    for (const { field, value: convertedValue } of converted) {
      if (structuredFilters[field]) continue;

      structuredFilters[field] = convertedValue;
    }
  }

  return convertStructureToFilters(structuredFilters, {
    [FilterSection.PROMOTIONS]: (value: FilterPromotion[]) =>
      `(${value.map((promotion) => getSingleFilter(FilterPromotionInventoryField[promotion], 'true')).join(' || ')})`,
    [InventoryField.modelYear]: (value: RangeFilterValue) =>
      getRangeFilter(InventoryField.modelYear, value?.min, value?.max),
    [InventoryField.slideOutsCount]: (value: number) =>
      getMultipleFilter(InventoryField.slideOutsCount, [value.toString()]),
    [InventoryField.isFlashSale]: (value: boolean) => getSingleFilter(InventoryField.isFlashSale, value.toString()),
    [InventoryField.isDailyDeal]: (value: boolean) => getSingleFilter(InventoryField.isDailyDeal, value.toString()),
    [InventoryField.length]: (value: { min?: number; max?: number }) =>
      getRangeFilter(InventoryField.length, value.min, value.max),
    [InventoryField.price]: (value: { min?: number; max?: number }) =>
      getRangeFilter(InventoryField.price, value.min, value.max),
    [InventoryField.mileage]: (value: { min?: number; max?: number }) =>
      getRangeFilter(InventoryField.mileage, value.min, value.max),
    [InventoryField.location]: (values: string[]) =>
      getMultipleFilter(InventoryField.location, values, false, false, true),
  });
};

export const formatPriceFilter = (value?: number) =>
  value !== undefined ? (Math.floor(value / 100) / 10).toFixed(1) + 'K' : '';

const getSortedUniqueOptions = (options?: string[]) => {
  if (!options || options.length === 0) return [];

  const addedFacets = new Set<string>();
  const result: string[] = [];
  for (const option of options) {
    const facetOption = getFacetCode(option);

    if (addedFacets.has(facetOption)) continue;

    addedFacets.add(facetOption);
    result.push(option);
  }

  return result.toSorted((a, b) => a.localeCompare(b));
};

const sectionOptionsGetterMap: Record<string, (baseFilters: BaseFilters) => string[]> = {
  [FilterSection.SORT_BY]: () => Object.values(SortBy),
  [FilterSection.PROMOTIONS]: () => Object.values(FilterPromotion),
  [FilterSection.CONDITION]: (baseFilters) => baseFilters[InventoryField.inventoryType]?.map(getInventoryType),
  [FilterSection.RV_TYPE]: (baseFilters) => baseFilters[InventoryField.body],
  [FilterSection.MAKE]: (baseFilters) => getSortedUniqueOptions(baseFilters[InventoryField.make]),
  [FilterSection.MODEL]: (baseFilters) => getSortedUniqueOptions(baseFilters[InventoryField.model]),
  [FilterSection.FLOOR_PLAN]: (baseFilters) => getSortedUniqueOptions(baseFilters[InventoryField.modelNumber]),
  [FilterSection.FEATURES]: (baseFilters) => getSortedUniqueOptions(baseFilters[InventoryField.equipment]),
  [FilterSection.LOCATION]: (baseFilters) => baseFilters[InventoryField.location],
  [FilterSection.FUEL_TYPE]: (baseFilters) => baseFilters[InventoryField.fuelType],
  [FilterSection.SLIDEOUTS]: (baseFilters) => baseFilters[InventoryField.slideOuts],
  [FilterSection.LENGTH]: (baseFilters) =>
    baseFilters[InventoryField.length] && ['0', ...baseFilters[InventoryField.length]],
  [FilterSection.PRICE]: (baseFilters) =>
    baseFilters[InventoryField.price] && ['0', ...baseFilters[InventoryField.price]],
  [FilterSection.YEAR]: (baseFilters) =>
    baseFilters[InventoryField.modelYear] || ['2015', (new Date().getFullYear() + 1).toString()],
};

export const getSectionOptions = (baseFilters: BaseFilters, section: FilterSection): string[] => {
  const optionsGetter = sectionOptionsGetterMap[section];
  const values = optionsGetter?.(baseFilters) || [];

  return values;
};

const sortByMap: Record<SortBy, string> = {
  [SortBy.RECOMMENDED]: `${InventoryField.order}:asc`,
  [SortBy.NEWEST]: `${InventoryField.modelYear}:desc`,
  [SortBy.OLDEST]: `${InventoryField.modelYear}:asc`,
  [SortBy.PRICE_LOW_TO_HIGH]: `${InventoryField.price}:asc`,
  [SortBy.PRICE_HIGH_TO_LOW]: `${InventoryField.price}:desc`,
};

export const convertSortBy = (value?: FilterValue) => {
  if (!value || !(value instanceof Array) || value.length === 0) return sortByMap[SortBy.RECOMMENDED];

  return sortByMap[value[0]] || sortByMap[SortBy.RECOMMENDED];
};
